// src/pages/pengamatan/buah-alami/hooks/useFormData.js
import { useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../../../firebase';

export const useFormData = (selectedPengamatanData) => {
  useEffect(() => {
    if (!selectedPengamatanData) return;

    console.log('📝 Mengisi data ke template form:', selectedPengamatanData);

    // Fungsi untuk mengambil signature dari tb_user berdasarkan uid
    const getSignatureFromUser = async (uid) => {
      if (!uid) {
        console.log('UID tidak ditemukan');
        return null;
      }

      try {
        // Referensi ke tb_user
        const userRef = ref(database, "db_user/tb_user");
        
        // Ambil semua data user
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          const userData = snapshot.val();
          console.log('Data user ditemukan:', Object.keys(userData).length, 'users');
          
          // Cari user dengan uid yang sesuai
          const foundUser = Object.values(userData).find(
            user => user.uid === uid
          );
          
          if (foundUser) {
            console.log('User ditemukan:', foundUser.name, 'UID:', foundUser.uid);
            if (foundUser.signatureBase64) {
              console.log('Signature ditemukan, panjang:', foundUser.signatureBase64.length);
              return foundUser.signatureBase64;
            } else {
              console.log('Signature tidak ditemukan untuk user:', foundUser.name);
            }
          } else {
            console.log('User dengan UID', uid, 'tidak ditemukan');
          }
        } else {
          console.log('Tidak ada data user di database');
        }
        
        return null;
      } catch (error) {
        console.error("Error fetching user signature:", error);
        return null;
      }
    };

    // Fungsi untuk menambahkan signature ke elemen
    const addSignatureToElement = (elementId, signatureBase64) => {
      const element = document.getElementById(elementId);
      if (element && signatureBase64) {
        // Buat elemen img untuk signature
        const img = document.createElement('img');
        img.src = `data:image/png;base64,${signatureBase64}`;
        img.alt = 'Signature';
        img.style.maxWidth = '150px';
        img.style.maxHeight = '60px';
        img.style.display = 'block';
        img.style.margin = '0 auto';
        
        // Kosongkan elemen dan tambahkan signature
        element.innerHTML = '';
        element.appendChild(img);
        console.log(`✅ Signature berhasil ditambahkan ke ${elementId}`);
      } else if (element) {
        console.warn(`❌ Signature tidak tersedia untuk ${elementId}`);
      } else {
        console.warn(`❌ Element ${elementId} tidak ditemukan`);
      }
    };

    // Fungsi untuk mengambil data status dari tb_lokasi di Realtime Database
    const fetchStatusLokasi = async (idLokasi) => {
      if (!idLokasi) {
        console.warn('❌ id_lokasi tidak tersedia');
        return '-';
      }

      try {
        console.log(`🔍 Mencari data lokasi untuk id_lokasi: ${idLokasi}`);
        
        // Query semua data di tb_lokasi
        const lokasiRef = ref(database, 'db_freshpine/tb_lokasi');
        const snapshot = await get(lokasiRef);

        if (snapshot.exists()) {
          const allLokasiData = snapshot.val();
          console.log('✅ Semua data lokasi ditemukan:', allLokasiData);
          
          // Cari data yang memiliki id_lokasi yang sesuai
          let foundLokasi = null;
          
          // Iterasi melalui semua key di tb_lokasi
          Object.keys(allLokasiData).forEach(key => {
            const lokasiItem = allLokasiData[key];
            if (lokasiItem && lokasiItem.id_lokasi === idLokasi) {
              foundLokasi = lokasiItem;
              console.log(`✅ Data lokasi ditemukan dengan key: ${key}`, foundLokasi);
            }
          });

          if (foundLokasi) {
            // Ambil status, jika tidak ada gunakan status_plantation
            const status = foundLokasi.status || foundLokasi.status_plantation || '-';
            return status;
          } else {
            console.warn('❌ Data lokasi tidak ditemukan untuk id_lokasi:', idLokasi);
            return '-';
          }
        } else {
          console.warn('❌ Tidak ada data di tb_lokasi');
          return '-';
        }
      } catch (error) {
        console.error('❌ Error fetching data lokasi:', error);
        return '-';
      }
    };

    // Format tanggal untuk tampilan
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return '-';
      try {
        const [day, month, year] = dateString.split('-');
        if (!day || !month || !year) return dateString || '-';
        const date = new Date(`${year}-${month}-${day}`);
        if (isNaN(date.getTime())) return dateString || '-';
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('id-ID', options);
      } catch (error) {
        return dateString || '-';
      }
    };

    // Fungsi untuk memformat angka sesuai kebutuhan
    const formatNumber = (value, type = 'default') => {
      if (!value && value !== 0) return '0';
      
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '0';
      
      switch (type) {
        case 'buah_alami':
        case 'total':
          return Math.round(numValue).toString();
        
        case 'populasi':
          // Ambil bagian integer saja
          const integerValue = Math.floor(numValue);
          
          // Format manual dengan separator ribuan
          return integerValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        
        default:
          return Math.round(numValue).toString();
      }
    };

    // Fungsi untuk merender data plot ke tabel
    const renderPlotDataToTable = (plotData, lokasi) => {
      const plotDataBody = document.getElementById('plot-data-body');
      if (!plotDataBody) {
        console.warn('❌ Element plot-data-body tidak ditemukan');
        return;
      }

      // Kosongkan tabel terlebih dahulu
      plotDataBody.innerHTML = '';

      // Urutkan plot berdasarkan key (01, 02, 03, dst)
      const sortedPlotKeys = Object.keys(plotData).sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        return numA - numB;
      });

      // Render setiap plot
      sortedPlotKeys.forEach((plotKey) => {
        const plot = plotData[plotKey];
        
        console.log(`🔍 Processing plot ${plotKey}:`, plot);

        // Data plot utama
        const dataEntry = plot.data_entry || {};
        const faseBunga = dataEntry.fase_bunga || {};
        const tanaman = dataEntry.tanaman || {};
        const penyakitTanaman = tanaman.penyakit_tanaman || {};
        const populasi = dataEntry.populasi || {};

        // Hitung % Buah Alami
        const totalTanaman = parseFloat(dataEntry.total_ba) || 0;
        const buahAlami = parseFloat(dataEntry.buah_alami_ba) || 0;
        const persenBuahAlami = totalTanaman > 0 ? ((buahAlami / totalTanaman) * 100).toFixed(2) + '%' : '0%';

        // 1. Render plot utama
        const mainRow = document.createElement('tr');
        mainRow.className = 'plot-data-row main-plot';
        mainRow.innerHTML = `
          <td class="td-form">${lokasi}</td>
          <td class="td-form">${plotKey}</td>
          <td class="td-form">${plot.luas_plot || '-'}</td>
          <td class="td-form">${plot.pjp || '-'}</td>

          <!-- Fase Bunga Alami -->
          <td class="td-form">${faseBunga.open_heart_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.red_bud_125_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.red_bud_254_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.early_cones_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.mid_cones_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.late_cones_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.early_petals_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.mid_petals_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.late_petals_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.dry_petals_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.filling_stage_1_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.filling_stage_2_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.sc0_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.sc0_ld_fb_ba || '0'}</td>

          <!-- Berbunga -->
          <td class="td-form">${faseBunga.terpetik_fb_ba || '0'}</td>
          <td class="td-form">${faseBunga.dimakan_tikus_fb_ba || '0'}</td>
          <td class="td-form">${tanaman.normal_t_ba || '0'}</td>
          <td class="td-form">${penyakitTanaman.phy_pt_ba || '0'}</td>
          <td class="td-form">${penyakitTanaman.erw_pt_ba || '0'}</td>
          <td class="td-form">${tanaman.kerdil_t_ba || '0'}</td>

          <!-- Buah Alami, Mati, Total, Spot Mati, % Buah Alami -->
          <td class="td-form">${formatNumber(dataEntry.buah_alami_ba, 'buah_alami')}</td>
          <td class="td-form">${dataEntry.mati_ba || '0'}</td>
          <td class="td-form">${formatNumber(dataEntry.total_ba, 'total')}</td>
          <td class="td-form">${dataEntry.spot_mati_ba || '0'}</td>
          <td class="td-form">${persenBuahAlami}</td>

          <!-- Populasi/Ha -->
          <td class="td-form">${formatNumber(populasi.normal_p_ba, 'populasi')}</td>
          <td class="td-form">${formatNumber(populasi.total_p_ba, 'populasi')}</td>
        `;

        plotDataBody.appendChild(mainRow);

        // 2. Render data tanaman_pinggiran (jika ada)
        const tanamanPinggiran = dataEntry.tanaman_pinggiran;
        console.log(`🔍 Tanaman pinggiran plot ${plotKey}:`, tanamanPinggiran);

        if (tanamanPinggiran && Object.keys(tanamanPinggiran).length > 0) {
          // Hitung % Buah Alami Pinggiran
          const totalTanamanPinggiran = parseFloat(tanamanPinggiran.total_tp_ba) || 0;
          const buahAlamiPinggiran = parseFloat(tanamanPinggiran.buah_alami_tp_ba) || 0;
          const persenBuahAlamiPinggiran = totalTanamanPinggiran > 0 ? 
            ((buahAlamiPinggiran / totalTanamanPinggiran) * 100).toFixed(2) + '%' : '0%';

          const pinggiranRow = document.createElement('tr');
          pinggiranRow.className = 'plot-data-row tanaman-pinggiran';

          // Data tanaman_pinggiran - PERHATIAN: field naming berbeda!
          const penyakitTanamanPinggiran = tanamanPinggiran.penyakit_tanaman_pinggiran || {};

          pinggiranRow.innerHTML = `
            <!-- Info Lokasi Pinggiran -->
            <td class="td-form">Pinggiran</td>
            <td class="td-form">${plotKey}</td>
            <td class="td-form">${plot.luas_plot || '-'}</td>
            <td class="td-form">-</td>

            <!-- Fase Bunga Alami Pinggiran (kosong) -->
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <td class="td-form">-</td>

            <!-- Berbunga Pinggiran -->
            <td class="td-form">-</td>
            <td class="td-form">-</td>
            <!-- PERBAIKAN: Gunakan field yang benar untuk tanaman_pinggiran -->
            <td class="td-form">${tanamanPinggiran.normal_tp_ba || '0'}</td>
            <td class="td-form">${penyakitTanamanPinggiran.phy_ptp_ba || '0'}</td>
            <td class="td-form">${penyakitTanamanPinggiran.erw_ptp_ba || '0'}</td>
            <td class="td-form">${tanamanPinggiran.kerdil_tp_ba || '0'}</td>

            <!-- Buah Alami, Mati, Total, Spot Mati, % Buah Alami Pinggiran -->
            <!-- PERBAIKAN: Gunakan field yang benar -->
            <td class="td-form">${formatNumber(tanamanPinggiran.buah_alami_tp_ba, 'buah_alami')}</td>
            <td class="td-form">${tanamanPinggiran.mati_tp_ba || '0'}</td>
            <td class="td-form">${formatNumber(tanamanPinggiran.total_tp_ba, 'total')}</td>
            <td class="td-form">-</td>
            <td class="td-form">${persenBuahAlamiPinggiran}</td>

            <!-- Populasi/Ha Pinggiran (kosong) -->
            <td class="td-form">-</td>
            <td class="td-form">-</td>
          `;

          plotDataBody.appendChild(pinggiranRow);
          console.log(`✅ BERHASIL menambahkan tanaman_pinggiran untuk plot ${plotKey}`);
        } else {
          console.log(`❌ TIDAK ADA data tanaman_pinggiran untuk plot ${plotKey}`);
        }
      });

      console.log(`✅ Berhasil merender ${sortedPlotKeys.length} plot ke tabel`);
    };

    // Fungsi utama untuk mengisi data
    const populateFormData = async () => {
      // 1. Tampilkan tanggal_pengamatan di id="hari-tanggal"
      const hariTanggalElement = document.getElementById('hari-tanggal');
      if (hariTanggalElement) {
        const formattedDate = formatDateForDisplay(selectedPengamatanData.tanggal_pengamatan);
        hariTanggalElement.textContent = formattedDate;
        console.log('✅ Mengisi hari-tanggal:', formattedDate);
      } else {
        console.warn('❌ Element hari-tanggal tidak ditemukan');
      }

      // 2. Tampilkan nama_pengamat di id="nama-pengamat" dan id="petugas-ppn"
      const namaPengamatElement = document.getElementById('nama-pengamat');
      const petugasPpnElement = document.getElementById('petugas-ppn');
      
      if (namaPengamatElement) {
        namaPengamatElement.textContent = selectedPengamatanData.nama_pengamat || '-';
        console.log('✅ Mengisi nama-pengamat:', selectedPengamatanData.nama_pengamat);
      } else {
        console.warn('❌ Element nama-pengamat tidak ditemukan');
      }
      
      if (petugasPpnElement) {
        petugasPpnElement.textContent = selectedPengamatanData.nama_pengamat || '-';
        console.log('✅ Mengisi petugas-ppn:', selectedPengamatanData.nama_pengamat);
      } else {
        console.warn('❌ Element petugas-ppn tidak ditemukan');
      }

      // 3. Tampilkan nama_mandor di id="mandor-ppn"
      const mandorPpnElement = document.getElementById('mandor-ppn');
      if (mandorPpnElement) {
        mandorPpnElement.textContent = selectedPengamatanData.nama_mandor || '-';
        console.log('✅ Mengisi mandor-ppn:', selectedPengamatanData.nama_mandor);
      } else {
        console.warn('❌ Element mandor-ppn tidak ditemukan');
      }

      // 4. Tampilkan data lainnya yang tersedia
      const bagianUnitKerjaElement = document.getElementById('bagian-unit-kerja');
      const ppnElement = document.getElementById('ppn');
      const statusLokasiElement = document.getElementById('status-lokasi');
      
      if (bagianUnitKerjaElement) {
        bagianUnitKerjaElement.textContent = selectedPengamatanData.bagian_unit_kerja || '-';
      }
      
      if (ppnElement) {
        ppnElement.textContent = selectedPengamatanData.ppn || '-';
      }
      
      // 5. Ambil dan tampilkan status lokasi dari tb_lokasi di Realtime Database
      if (statusLokasiElement) {
        const statusLokasi = await fetchStatusLokasi(selectedPengamatanData.id_lokasi);
        statusLokasiElement.textContent = statusLokasi;
        console.log('✅ Mengisi status-lokasi:', statusLokasi);
      } else {
        console.warn('❌ Element status-lokasi tidak ditemukan');
      }

      // 6. Tampilkan data plot ke tabel
      if (selectedPengamatanData.plot) {
        console.log('📊 Data plot tersedia:', Object.keys(selectedPengamatanData.plot).length, 'plot');
        renderPlotDataToTable(selectedPengamatanData.plot, selectedPengamatanData.lokasi);
      }

      // 7. Ambil dan tampilkan signature
      console.log('🖊️ Mengambil signature...');
      
      // Ambil UID dari data yang dipilih
      const uidPengamat = selectedPengamatanData.uid_pengamat;
      const uidMandor = selectedPengamatanData.uid_mandor;

      console.log('UID Pengamat:', uidPengamat);
      console.log('UID Mandor:', uidMandor);

      // Ambil signature untuk petugas PPN (pengamat)
      if (uidPengamat) {
        const signaturePengamat = await getSignatureFromUser(uidPengamat);
        addSignatureToElement('signature-petugas-ppn', signaturePengamat);
      } else {
        console.warn('❌ UID Pengamat tidak tersedia');
      }

      // Ambil signature untuk mandor PPN
      if (uidMandor) {
        const signatureMandor = await getSignatureFromUser(uidMandor);
        addSignatureToElement('signature-mandor-ppn', signatureMandor);
      } else {
        console.warn('❌ UID Mandor tidak tersedia');
      }
    };

    // Jalankan fungsi utama
    populateFormData();

  }, [selectedPengamatanData]);

  return null;
};